import { displayName, entityId } from "@/api/client";

const toNumber = (value) => {
  if (value === null || value === undefined || value === "") return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const coordinatePair = (source) => {
  if (!source || typeof source !== "object") return null;
  const geo = source.coordinates || source.geo?.coordinates;
  if (Array.isArray(geo) && geo.length >= 2) {
    const lng = toNumber(geo[0]); const lat = toNumber(geo[1]);
    if (lat !== null && lng !== null) return { lat, lng };
  }
  const lat = toNumber(source.latitude ?? source.lat);
  const lng = toNumber(source.longitude ?? source.lng ?? source.lon);
  return lat !== null && lng !== null ? { lat, lng } : null;
};

export const getCoordinates = (item) => {
  const sources = [item, item?.location, item?.last_location, item?.last_online_location, item?.last_known_location, item?.geo];
  const coordinates = sources.map(coordinatePair).find(Boolean);
  if (!coordinates || Math.abs(coordinates.lat) > 90 || Math.abs(coordinates.lng) > 180) return null;
  return coordinates;
};

const normalizedPlatform = (value) => {
  const platform = String(value || "").toLowerCase();
  return ["ios", "android", "web"].includes(platform) ? platform : null;
};

export const normalizeMapUser = (user, index = 0) => ({
  userId: entityId(user, "user_id") || `unknown-${index}`,
  name: user.name || user.display_name || displayName(user),
  username: user.username || null,
  email: user.email || null,
  avatar: user.avatar || user.avatar_url || user.photo_url || null,
  role: user.role || "consumer",
  isCreator: user.is_creator === true,
  idVerified: Boolean(user.id_verified),
  banned: Boolean(user.banned || user.is_banned || user.status === "banned"),
  position: getCoordinates(user),
  city: user.city || user.location?.city || user.last_location?.city || null,
  country: user.country || user.location?.country || user.last_location?.country || null,
  platform: normalizedPlatform(user.platform),
  lastOnline: user.last_online || user.last_online_at || user.last_seen_at || user.updated_at || null,
  isLive: user.is_live === true,
  liveId: user.live_id || null,
  raw: user,
});

const liveOwnerId = (live) =>
  live?.user_id || live?.host_id || live?.creator_id || entityId(live?.host || live?.user || live?.creator, "user_id");

const liveDetails = (user, live) => live || (user.isLive ? {
  live_id: user.liveId,
  status: "live",
  title: "Active live session",
} : null);

export const buildMapPoints = (users, liveSessions) => {
  const liveByUser = new Map();
  liveSessions.forEach((live) => {
    const ownerId = liveOwnerId(live);
    if (ownerId) liveByUser.set(String(ownerId), live);
  });

  const representedLive = new Set();
  const points = users.flatMap((user) => {
    const matchedLive = liveByUser.get(String(user.userId));
    const live = liveDetails(user, matchedLive);
    const position = getCoordinates(matchedLive) || user.position;
    if (!position) return [];
    const liveId = entityId(live, "live_id", "stream_id") || user.liveId;
    if (liveId) representedLive.add(String(liveId));
    return [{
      key: `user-${user.userId}`,
      userId: user.userId,
      liveId,
      category: live ? "live" : user.isCreator ? "creator" : "user",
      isCreator: user.isCreator,
      isLive: Boolean(live),
      position,
      name: user.name,
      username: user.username,
      email: user.email,
      avatar: user.avatar,
      role: user.role,
      idVerified: user.idVerified,
      platform: user.platform || normalizedPlatform(matchedLive?.host?.platform),
      city: matchedLive?.city || user.city,
      country: matchedLive?.country || user.country,
      lastOnline: user.lastOnline || matchedLive?.started_at || matchedLive?.created_at,
      banned: user.banned,
      user,
      live,
    }];
  });

  liveSessions.forEach((live, index) => {
    const liveId = entityId(live, "live_id", "stream_id") || `live-${index}`;
    if (representedLive.has(String(liveId))) return;
    const position = getCoordinates(live);
    if (!position) return;
    const host = normalizeMapUser({ ...(live.host || live.user || live.creator || {}), user_id: liveOwnerId(live) }, index);
    points.push({
      key: `live-${liveId}`,
      userId: liveOwnerId(live),
      liveId,
      category: "live",
      isCreator: ["creator", "verified_creator"].includes(host.role),
      isLive: true,
      position,
      name: host.name,
      username: host.username,
      email: host.email,
      avatar: host.avatar,
      role: host.role,
      idVerified: host.idVerified,
      platform: host.platform,
      city: live.city || host.city,
      country: live.country || host.country,
      lastOnline: live.started_at || live.created_at,
      banned: host.banned,
      user: host,
      live,
    });
  });
  return points;
};

export const matchesMapFilters = (item, category, platform, query) => {
  const categoryMatch = category === "all" ||
    (category === "live" && (item.isLive || item.category === "live")) ||
    (category === "creator" && item.isCreator === true) ||
    (category === "user" && item.isCreator !== true);
  const platformMatch = platform === "all" || (platform === "unknown" ? !item.platform : item.platform === platform);
  const search = query.trim().toLowerCase();
  const queryMatch = !search || [item.userId, item.name, item.username, item.email, item.city, item.country]
    .some((value) => String(value || "").toLowerCase().includes(search));
  return categoryMatch && platformMatch && queryMatch;
};